/**
 * useDeviceType Hook
 *
 * Smart device detection using multi-signal approach:
 * 1. PRIMARY: Pointer media query (coarse = touch/mobile, fine = mouse/desktop)
 * 2. SECONDARY: Viewport width (< 1024px suggests mobile)
 * 3. TERTIARY: Touch capability confirmation
 * 4. FALLBACK: User Agent string
 *
 * Edge case handling:
 * - Surface Pro laptop mode → fine pointer + hover → desktop
 * - Surface Pro tablet mode → coarse pointer + no hover → tablet
 * - iPad Pro → coarse pointer + no hover → tablet
 * - Desktop with touchscreen → fine primary pointer (mouse) → desktop
 * - DevTools mobile emulation → falls through to width check
 * - 2-in-1 mode switch → pointer media query change event triggers re-detection
 */

import { useState, useEffect, useMemo } from 'react';

const BREAKPOINTS = {
  mobile: 0,
  tablet: 768,
  desktop: 1024,
} as const;

export type DeviceType = 'mobile' | 'tablet' | 'desktop';
export type DetectionMethod = 'pointer-query' | 'user-agent' | 'screen-width' | 'touch' | 'ssr-fallback';

export interface DeviceTypeHookReturn {
  isMobile: boolean;
  isDesktop: boolean;
  isTablet: boolean;
  deviceType: DeviceType;
  detectionMethod: DetectionMethod;
  width: number;
  height: number;
  hasTouch: boolean;
  isPortrait: boolean;
  isCoarsePointer: boolean;
  hasHover: boolean;
}

/**
 * PRIMARY: Detect device from pointer media queries.
 * Most reliable signal because it detects the actual input method in use.
 */
function detectDeviceFromPointer(width: number): { type: DeviceType; method: DetectionMethod } | null {
  if (typeof window === 'undefined') return null;

  const isCoarse = window.matchMedia('(pointer: coarse)').matches;
  const isFine = window.matchMedia('(pointer: fine)').matches;
  const hasHover = window.matchMedia('(hover: hover)').matches;

  // Fine pointer + hover = mouse/trackpad → desktop
  if (isFine && hasHover && !isCoarse) {
    return { type: 'desktop', method: 'pointer-query' };
  }

  // Coarse pointer + no hover = touch-only → mobile or tablet by width
  if (isCoarse && !hasHover) {
    return {
      type: width < BREAKPOINTS.tablet ? 'mobile' : 'tablet',
      method: 'pointer-query',
    };
  }

  // Coarse + hover = touch device with hover (e.g., 2-in-1 with mouse attached)
  if (isCoarse && hasHover) {
    const hasAnyFine = window.matchMedia('(any-pointer: fine)').matches;
    if (hasAnyFine) {
      // Has both coarse and fine pointers → they have a mouse, treat as desktop
      return { type: 'desktop', method: 'pointer-query' };
    }
    return {
      type: width < BREAKPOINTS.tablet ? 'mobile' : 'tablet',
      method: 'pointer-query',
    };
  }

  // Fine pointer + no hover (rare)
  if (isFine && !hasHover) {
    return { type: 'desktop', method: 'pointer-query' };
  }

  return null;
}

function detectDeviceFromScreenWidth(width: number): DeviceType {
  if (width < BREAKPOINTS.tablet) return 'mobile';
  if (width < BREAKPOINTS.desktop) return 'tablet';
  return 'desktop';
}

function hasTouchSupport(): boolean {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

function detectDeviceFromUserAgent(): DeviceType | null {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent.toLowerCase();

  const tabletPatterns = [/ipad/, /android(?!.*mobile)/, /tablet/, /kindle/, /silk/];
  const mobilePatterns = [/android.*mobile/, /iphone/, /ipod/, /windows phone/, /mobile/];

  if (tabletPatterns.some(p => p.test(ua))) return 'tablet';
  if (mobilePatterns.some(p => p.test(ua))) return 'mobile';
  return 'desktop';
}

function determineDeviceType(width: number): {
  deviceType: DeviceType;
  detectionMethod: DetectionMethod;
} {
  // PRIMARY: Pointer media queries
  const pointerResult = detectDeviceFromPointer(width);
  if (pointerResult) {
    return { deviceType: pointerResult.type, detectionMethod: pointerResult.method };
  }

  // SECONDARY: Screen width + touch
  const screenType = detectDeviceFromScreenWidth(width);
  const hasTouch = hasTouchSupport();
  if (screenType === 'mobile' && hasTouch) {
    return { deviceType: 'mobile', detectionMethod: 'touch' };
  }

  // TERTIARY: User agent
  const uaType = detectDeviceFromUserAgent();
  if (uaType && uaType !== 'desktop') {
    return { deviceType: uaType, detectionMethod: 'user-agent' };
  }

  return { deviceType: screenType, detectionMethod: 'screen-width' };
}

export function useDeviceType(): DeviceTypeHookReturn {
  const [dimensions, setDimensions] = useState(() => {
    if (typeof window === 'undefined') return { width: 0, height: 0 };
    return { width: window.innerWidth, height: window.innerHeight };
  });

  // Track pointer state reactively so 2-in-1 devices re-evaluate on mode switch
  const [pointerState, setPointerState] = useState(() => {
    if (typeof window === 'undefined') return { isCoarse: false, hasHover: true };
    return {
      isCoarse: window.matchMedia('(pointer: coarse)').matches,
      hasHover: window.matchMedia('(hover: hover)').matches,
    };
  });

  const { deviceType, detectionMethod } = useMemo(() => {
    if (typeof window === 'undefined') {
      return { deviceType: 'mobile' as DeviceType, detectionMethod: 'ssr-fallback' as DetectionMethod };
    }
    return determineDeviceType(dimensions.width);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, pointerState.isCoarse, pointerState.hasHover]);

  const hasTouch = useMemo(() => hasTouchSupport(), []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let resizeTimeout: ReturnType<typeof setTimeout>;

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        setDimensions({ width: window.innerWidth, height: window.innerHeight });
      }, 150);
    };

    // Listen for pointer media query changes (Surface Pro switching modes, etc.)
    const pointerQuery = window.matchMedia('(pointer: coarse)');
    const hoverQuery = window.matchMedia('(hover: hover)');

    const handlePointerChange = () => {
      setPointerState({
        isCoarse: window.matchMedia('(pointer: coarse)').matches,
        hasHover: window.matchMedia('(hover: hover)').matches,
      });
    };

    window.addEventListener('resize', handleResize);
    pointerQuery.addEventListener('change', handlePointerChange);
    hoverQuery.addEventListener('change', handlePointerChange);

    handleResize();

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      pointerQuery.removeEventListener('change', handlePointerChange);
      hoverQuery.removeEventListener('change', handlePointerChange);
    };
  }, []);

  const { width, height } = dimensions;
  const isMobile = deviceType === 'mobile';
  const isTablet = deviceType === 'tablet';
  const isDesktop = deviceType === 'desktop';
  const isPortrait = height > width;

  return {
    isMobile,
    isDesktop,
    isTablet,
    deviceType,
    detectionMethod,
    width,
    height,
    hasTouch,
    isPortrait,
    isCoarsePointer: pointerState.isCoarse,
    hasHover: pointerState.hasHover,
  };
}

export function useDeviceTypeSimple(): DeviceType {
  const { deviceType } = useDeviceType();
  return deviceType;
}

export function useBreakpoint(breakpoint: keyof typeof BREAKPOINTS): boolean {
  const { width } = useDeviceType();
  return width >= BREAKPOINTS[breakpoint];
}

export function useIsMobileDevice(): boolean {
  const { isMobile, isTablet } = useDeviceType();
  return isMobile || isTablet;
}

export { BREAKPOINTS };
